import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Rocket, Bell, LogOut, Plus, LayoutDashboard } from "lucide-react";

export function PortalShell({ user, title, children }) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [bellOpen, setBellOpen] = useState(false);

  const loadNotifications = useCallback(() => {
    api.get("/portal/notifications").then((r) => setNotifications(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadNotifications();
    const t = setInterval(loadNotifications, 20000);
    return () => clearInterval(t);
  }, [loadNotifications]);

  const unread = notifications.filter((n) => !n.read).length;

  const openBell = async () => {
    setBellOpen((v) => !v);
    if (!bellOpen && unread > 0) {
      await api.put("/portal/notifications/read-all").catch(() => {});
      loadNotifications();
    }
  };

  const logout = async () => {
    await api.post("/portal/auth/logout").catch(() => {});
    navigate("/portal");
  };

  return (
    <div className="min-h-screen" data-testid="portal-shell">
      <header className="sticky top-0 z-40 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <Link to={user?.role === "specialist" ? "/specialist" : "/dashboard"} className="flex items-center gap-2.5" data-testid="portal-brand">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#FF8800] to-[#ff5e00] flex items-center justify-center">
              <Rocket className="w-4 h-4 text-[#06080E]" strokeWidth={2.4} />
            </div>
            <div>
              <p className="font-display font-bold text-white leading-none text-sm">
                LAUNCH<span className="text-[#FF8800]">LAB</span> {user?.role === "specialist" ? "SPECIALIST" : "PORTAL"}
              </p>
              <p className="text-[10px] font-mono-tech tracking-widest text-slate-500 uppercase mt-0.5">{title}</p>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            {user?.role === "founder" && (
              <>
                <Link
                  to="/dashboard"
                  data-testid="portal-nav-dashboard"
                  className="hidden sm:inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
                >
                  <LayoutDashboard className="w-3.5 h-3.5" /> Dashboard
                </Link>
                <Link
                  to="/dashboard/new"
                  data-testid="portal-nav-new-startup"
                  className="hidden sm:inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" /> New Startup
                </Link>
              </>
            )}
            <div className="relative">
              <button
                data-testid="portal-notifications"
                onClick={openBell}
                className="w-9 h-9 rounded-lg glass flex items-center justify-center text-slate-300 hover:text-white transition-colors relative"
                aria-label="Notifications"
              >
                <Bell className="w-4 h-4" />
                {unread > 0 && (
                  <span className="absolute -top-1 -right-1 w-4.5 h-4.5 min-w-[18px] px-1 rounded-full bg-[#FF8800] text-[#06080E] text-[10px] font-bold flex items-center justify-center" data-testid="portal-notifications-count">
                    {unread}
                  </span>
                )}
              </button>
              {bellOpen && (
                <div className="absolute right-0 top-full mt-2 w-80 glass rounded-xl p-3 max-h-80 overflow-y-auto shadow-[0_20px_50px_rgba(0,0,0,0.5)]" data-testid="notifications-dropdown">
                  {notifications.length === 0 ? (
                    <p className="text-xs text-slate-500 text-center py-6">No notifications yet.</p>
                  ) : (
                    notifications.map((n) => (
                      <div key={n.id} className={`px-3 py-2.5 rounded-lg text-xs mb-1.5 ${n.read ? "text-slate-400" : "text-slate-100 bg-white/5"}`}>
                        <p className="leading-relaxed">{n.text}</p>
                        <p className="text-[10px] text-slate-600 mt-1">{new Date(n.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            <button
              data-testid="portal-logout"
              onClick={logout}
              className="w-9 h-9 rounded-lg glass flex items-center justify-center text-slate-300 hover:text-red-400 transition-colors"
              aria-label="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">{children}</main>
    </div>
  );
}
